import React from "react";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/store";
import { toast } from "sonner";
import { Calendar, Camera, Store, ClipboardList, Instagram, MessageCircle, Lock } from "lucide-react";

const TYPES = [
  { k: "retirada", icon: Store, t: "Retirada presencial", s: "Agende um horário para retirar seu pedido." },
  { k: "sessao_foto", icon: Camera, t: "Sessão fotográfica", s: "Sessão com nossa fotógrafa parceira Najyla." },
  { k: "consulta_orcamento", icon: ClipboardList, t: "Consulta/orçamento", s: "Vamos planejar seu projeto especial." },
];

export default function Appointments() {
  const { user, loading: authLoading } = useAuth();

  if (authLoading) return <div className="py-24 text-center text-[#6B7280]">Carregando…</div>;
  if (!user) return <Navigate to="/entrar?next=/agendar" replace state={{ reason: "appointment" }} />;

  return <AppointmentsForm user={user} />;
}

function AppointmentsForm({ user }) {
  const [form, setForm] = useState({
    customer_name: user.name || "", customer_email: user.email || "", customer_phone: user.phone || "",
    appointment_type: "sessao_foto", scheduled_at: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // result from API

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/appointments", form);
      setDone(data);
      toast.success("Agendamento solicitado!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao agendar");
    } finally { setLoading(false); }
  };

  if (done) {
    const isPhoto = done.appointment_type === "sessao_foto" && done.photographer_wa_link;
    return (
      <div className="mx-auto max-w-2xl px-6 py-24" data-testid="appointment-done">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-[#F7F2E7] flex items-center justify-center">
            <Calendar size={34} className="text-[#D4AF37]" />
          </div>
          <h1 className="mt-6 font-display text-4xl text-[#0D1B2A]">
            {isPhoto ? "Vamos criar arte juntas!" : "Recebemos sua solicitação"}
          </h1>
          <p className="mt-3 text-[#3A4A5F]">
            {isPhoto
              ? `Sua sessão fotográfica será conduzida por ${done.photographer_name}, nossa fotógrafa parceira. Fale com ela pelo WhatsApp para confirmar detalhes.`
              : "Em breve confirmaremos seu horário pelo WhatsApp."}
          </p>
        </div>

        {isPhoto ? (
          <div className="mt-10 bg-white rounded-3xl border border-[#0D1B2A]/5 p-6" data-testid="photographer-card">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#F7F2E7] flex items-center justify-center">
                <Camera size={28} className="text-[#D4AF37]" />
              </div>
              <div>
                <div className="eyebrow">Fotógrafa parceira</div>
                <div className="font-display text-2xl text-[#0D1B2A]">{done.photographer_name}</div>
                {done.photographer_instagram && (
                  <a href={`https://instagram.com/${done.photographer_instagram}`} target="_blank" rel="noreferrer" className="text-xs text-[#D4AF37] inline-flex items-center gap-1 mt-1">
                    <Instagram size={12} /> @{done.photographer_instagram}
                  </a>
                )}
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a data-testid="photographer-wa-btn" href={done.photographer_wa_link} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-3 rounded-full font-medium">
                <MessageCircle size={18} /> Falar com {done.photographer_name} no WhatsApp
              </a>
              {done.photographer_instagram && (
                <a href={`https://instagram.com/${done.photographer_instagram}`} target="_blank" rel="noreferrer"
                  className="btn-outline-navy inline-flex items-center gap-2">
                  <Instagram size={16} /> Ver portfólio
                </a>
              )}
            </div>
            <p className="mt-6 text-xs text-[#6B7280]">
              A Eterniza já foi notificada do seu agendamento. Nossa parceira Najyla continuará o atendimento personalizado.
            </p>
          </div>
        ) : (
          <div className="mt-8 text-center">
            <a href="https://wa.me/5542984285953" target="_blank" rel="noreferrer" className="btn-gold inline-flex items-center gap-2">
              <MessageCircle size={16} /> Falar com Karine
            </a>
          </div>
        )}
      </div>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-8 py-14" data-testid="appointments-root">
      <div className="text-center">
        <div className="eyebrow">Agende com a gente</div>
        <h1 className="mt-3 font-display text-4xl md:text-5xl text-[#0D1B2A]">
          Vamos <em className="font-script text-[#D4AF37] not-italic">conversar</em>?
        </h1>
        <p className="mt-3 text-[#3A4A5F]">Escolha o tipo de atendimento e a melhor data.</p>
      </div>

      <div className="mt-10 grid sm:grid-cols-3 gap-4">
        {TYPES.map(({ k, icon: I, t, s }) => (
          <button
            key={k}
            type="button"
            data-testid={`appt-type-${k}`}
            onClick={() => set("appointment_type", k)}
            className={`text-left p-5 rounded-2xl border transition-colors ${
              form.appointment_type === k ? "border-[#D4AF37] bg-[#F7F2E7]" : "border-[#0D1B2A]/10 bg-white hover:border-[#D4AF37]/60"
            }`}
          >
            <I size={20} className="text-[#0D1B2A]" />
            <div className="mt-3 font-display text-lg text-[#0D1B2A]">{t}</div>
            <div className="text-xs text-[#6B7280] mt-1">{s}</div>
          </button>
        ))}
      </div>

      {form.appointment_type === "sessao_foto" && (
        <div className="mt-6 bg-[#F7F2E7] border border-[#D4AF37]/40 rounded-2xl p-4 flex items-center gap-3 text-sm text-[#0D1B2A]" data-testid="photographer-hint">
          <Camera size={18} className="text-[#D4AF37] shrink-0" />
          <div>Sua sessão será conduzida pela <b>Najyla</b> (<a className="text-[#D4AF37]" href="https://instagram.com/nsantos.studio" target="_blank" rel="noreferrer">@nsantos.studio</a>), nossa fotógrafa parceira. Após enviar, você conversa com ela direto pelo WhatsApp.</div>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 bg-white rounded-3xl border border-[#0D1B2A]/5 p-6 lg:p-8 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm text-[#3A4A5F]">Nome</label>
            <input data-testid="appt-name" required className="input-luxe mt-1" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} /></div>
          <div><label className="text-sm text-[#3A4A5F]">WhatsApp</label>
            <input data-testid="appt-phone" required className="input-luxe mt-1" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} /></div>
          <div><label className="text-sm text-[#3A4A5F]">E-mail</label>
            <input data-testid="appt-email" required type="email" className="input-luxe mt-1" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} /></div>
          <div><label className="text-sm text-[#3A4A5F]">Data e horário desejados</label>
            <input data-testid="appt-datetime" required type="datetime-local" className="input-luxe mt-1" value={form.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} /></div>
        </div>
        <div><label className="text-sm text-[#3A4A5F]">Detalhes</label>
          <textarea data-testid="appt-notes" className="input-luxe mt-1" rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Conte mais sobre o que você imagina..." /></div>
        <button data-testid="appt-submit" disabled={loading} className="btn-gold w-full py-3">
          {loading ? "Enviando…" : "Solicitar agendamento"}
        </button>
      </form>
    </div>
  );
}
